"use client";

import { useQuery } from "@tanstack/react-query";
import gql from "graphql-tag";
import type { GeneratedQuery } from "@aws-amplify/api-graphql";
import { fetchGraphQLClientWithApiKey } from "../../../amplify/fetchGraphQLClient";

/** Amazon Location bills per request; don't query on one or two characters. */
export const MIN_ADDRESS_QUERY_LENGTH = 3;

export interface AddressSuggestion {
  placeId: string;
  label: string;
}

export interface ResolvedAddress {
  address: string;
  city: string;
  state: string;
  zipCode: string;
}

interface SearchAddressesQueryVariables {
  text: string;
}

interface SearchAddressesQueryData {
  searchAddresses: AddressSuggestion[];
}

export const searchAddressesQuery = gql`
  query searchAddresses($text: String!) {
    searchAddresses(text: $text) {
      placeId
      label
    }
  }
`.loc?.source.body as GeneratedQuery<
  SearchAddressesQueryVariables,
  SearchAddressesQueryData
>;

interface ResolveAddressQueryVariables {
  placeId: string;
}

interface ResolveAddressQueryData {
  resolveAddress: ResolvedAddress | null;
}

export const resolveAddressQuery = gql`
  query resolveAddress($placeId: String!) {
    resolveAddress(placeId: $placeId) {
      address
      city
      state
      zipCode
    }
  }
`.loc?.source.body as GeneratedQuery<
  ResolveAddressQueryVariables,
  ResolveAddressQueryData
>;

interface UseSearchAddressesOptions {
  /** The (already debounced) search text from the autocomplete input. */
  text: string;
  enabled?: boolean;
}

/**
 * Address typeahead for the intake address block. Goes through the api-key
 * client because the sign-up step that uses it runs before any Cognito session
 * exists.
 */
export const useSearchAddresses = ({
  text,
  enabled = true,
}: UseSearchAddressesOptions) => {
  const trimmed = text.trim();
  return useQuery<AddressSuggestion[]>({
    queryKey: ["addressSuggestions", trimmed],
    queryFn: async () => {
      const response = await fetchGraphQLClientWithApiKey(
        searchAddressesQuery,
        { text: trimmed }
      );
      return response.data?.searchAddresses ?? [];
    },
    enabled: enabled && trimmed.length >= MIN_ADDRESS_QUERY_LENGTH,
    // The same prefix yields the same suggestions for the length of a session.
    staleTime: 5 * 60 * 1000,
  });
};

/**
 * Resolves a chosen suggestion into storable address components. Imperative
 * rather than a query hook because it fires on selection, not on render.
 * Returns null on any failure so the caller falls back to manual entry.
 */
export const resolveAddress = async (
  placeId: string
): Promise<ResolvedAddress | null> => {
  try {
    const response = await fetchGraphQLClientWithApiKey(resolveAddressQuery, {
      placeId,
    });
    return response.data?.resolveAddress ?? null;
  } catch (error) {
    console.error("Error resolving the selected address", error);
    return null;
  }
};
